import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { StreamEventSchema, type EventLeaf } from '@immo/api-contract';
import { apiClient, refreshAccessToken } from '@/lib/auth/api-client';
import { getAccessToken } from '@/lib/auth/access-token-store';
import { readSseStream, safeJsonParse } from '@/lib/sse/read-sse-stream';
import { reconnectDelayMs } from './reconnect-backoff';
import { WorkspaceEventsContext, type WorkspaceEventsContextValue } from './context';

type PayloadHandler = (payload: unknown) => void;

type Subscription = {
  readonly schema: EventLeaf['schema'];
  readonly handlers: Set<PayloadHandler>;
};

type ReconnectSubscriber = { handler: () => void; seenConnect: boolean };

export type WorkspaceEventsProviderProps = {
  /**
   * The RESOLVED workspace UUID from the `requireWorkspace` loader context — not
   * the `:workspaceId` route param, which carries the workspace SLUG on every
   * canonical `/w/:slug/…` URL and would fail the route's `z.uuid()` validation.
   */
  readonly workspaceId: string;
  readonly children: ReactNode;
  /** Injectable for tests; defaults to the global `fetch`. */
  readonly fetchImpl?: typeof fetch;
};

/**
 * One SSE connection per workspace tab, fanned out to whichever components have
 * registered for an event's name. An event nobody subscribed to costs a failed
 * map lookup, which is why an unmounted surface is free.
 */
export function WorkspaceEventsProvider({ workspaceId, children, fetchImpl }: WorkspaceEventsProviderProps) {
  const [connected, setConnected] = useState(false);
  const subscriptionsRef = useRef(new Map<string, Subscription>());
  const reconnectSubscribersRef = useRef(new Set<ReconnectSubscriber>());
  const connectedRef = useRef(false);

  const subscribe = useCallback((leaf: EventLeaf, handler: PayloadHandler) => {
    const existing = subscriptionsRef.current.get(leaf.type);
    const subscription = existing ?? { schema: leaf.schema, handlers: new Set<PayloadHandler>() };

    subscription.handlers.add(handler);
    subscriptionsRef.current.set(leaf.type, subscription);

    return () => {
      subscription.handlers.delete(handler);

      if (subscription.handlers.size === 0) subscriptionsRef.current.delete(leaf.type);
    };
  }, []);

  const subscribeReconnect = useCallback((handler: () => void) => {
    // A surface that mounts while the stream is already up has NOT loaded its data
    // through this connection, so its next connect is a reconnect and must refetch.
    // A surface that mounts while it is down has just loaded its own data, so the
    // connect that follows is its first and must not double every initial request.
    const subscriber: ReconnectSubscriber = { handler, seenConnect: connectedRef.current };
    reconnectSubscribersRef.current.add(subscriber);

    return () => {
      reconnectSubscribersRef.current.delete(subscriber);
    };
  }, []);

  const url = apiClient.workspaceEvents.stream({ pathParams: { workspaceId } });

  useEffect(() => {
    const controller = new AbortController();
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const dispatch = (raw: string): void => {
      const frame = StreamEventSchema.safeParse(safeJsonParse(raw));

      if (!frame.success) return;

      const subscription = subscriptionsRef.current.get(frame.data.name);

      if (subscription === undefined) return;

      const payload = subscription.schema.safeParse(frame.data.payload);

      if (!payload.success) {
        if (!import.meta.env.PROD) {
          console.warn(`workspace event ${frame.data.name} dropped: payload does not match its contract schema`);
        }

        return;
      }

      // Iterate a snapshot: a handler may unmount a sibling subscriber.
      for (const handler of [...subscription.handlers]) handler(payload.data);
    };

    const announceConnected = (): void => {
      attempt = 0;
      connectedRef.current = true;
      setConnected(true);

      for (const subscriber of [...reconnectSubscribersRef.current]) {
        if (subscriber.seenConnect) subscriber.handler();
        else subscriber.seenConnect = true;
      }
    };

    const run = (): void => {
      void readSseStream({
        url,
        token: getAccessToken(),
        signal: controller.signal,
        refresh: refreshAccessToken,
        onOpen: announceConnected,
        onPayload: dispatch,
        ...(fetchImpl ? { fetchImpl } : {}),
      })
        .catch(() => {
          // A failed connect is retried below like any other end of stream.
        })
        .finally(() => {
          if (controller.signal.aborted) return;

          connectedRef.current = false;
          setConnected(false);
          timer = setTimeout(run, reconnectDelayMs(attempt));
          attempt += 1;
        });
    };

    run();

    return () => {
      controller.abort();

      if (timer !== undefined) clearTimeout(timer);

      connectedRef.current = false;
      setConnected(false);
    };
  }, [url, fetchImpl]);

  const value = useMemo<WorkspaceEventsContextValue>(
    () => ({ connected, subscribe, subscribeReconnect }),
    [connected, subscribe, subscribeReconnect],
  );

  return <WorkspaceEventsContext.Provider value={value}>{children}</WorkspaceEventsContext.Provider>;
}
